
import { defineComponent } from 'vue';
import Calendar from '@/components/Calendar.vue';
import { Slot, CalendarDay, CarBrand, SelectComponentItem } from '@/utils/interfaces';
import { SlotStatus } from '@/utils/enums';
import { getDateFrom, mapCalendarSlots } from '@/utils/helpers';
import HTTP from '@/config/axios-instance';
import AutobookingFormSkeleton from '@/components/autobooking-form/AutobookingFormSkeleton.vue';
import { DmButton, DmDatalist, DmSelect, DmTextarea } from '@dobrymechanik/vue-ui-v2';

const fieldInit: SelectComponentItem = { key: '', text: '' };

interface IMechanicMeta {
  lat: string;
  lng: string;
  address: string;
  zilo_id: string;
}

const bookowarkaURL = process.env.VUE_APP_BOOKOWARKA_URL || 'https://dobrymechanik.pl/umow';

export default defineComponent({
  name: 'AutobookingFormWidget',
  components: {
    Calendar,
    AutobookingFormSkeleton,
    DmButton,
    DmDatalist,
    DmSelect,
    DmTextarea
  },
  $refs!: {
    calendar: HTMLDivElement
  },
  props: {
    dataMechanicId: {
      type: Number,
      default: 0
    },
    dataWebColumns: {
      type: Number,
      default: 3
    },
    dataInitialDate: {
      type: String,
      default: ''
    },
    dataInitialService: {
      type: String,
      default: ''
    },
    dataSlotsInterval: {
      type: Number,
      default: 0
    },
    repairReasonId: {
      type: String,
      default: ''
    },
    services: {
      type: Array,
      default: () => {
        return [];
      }
    },
    initialCategoryId: {
      type: String,
      default: ''
    },
    dataBrand: {
      type: String,
      default: ''
    },
    dataBrandId: {
      type: String,
      default: ''
    },
    dataModel: {
      type: String,
      default: ''
    },
    dataModelId: {
      type: String,
      default: ''
    },
    dataMechanicMeta: {
      type: String,
      default: ''
    },
    dataBookowarkaFlow: {
      type: String,
      default: ''
    },
    dataFullAutobookingSupport: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      brand: fieldInit as SelectComponentItem,
      model: fieldInit as SelectComponentItem,
      service: fieldInit as SelectComponentItem,
      notes: '' as string,
      slotsSelected: [] as Slot[],
      carBrowser: {
        brands: [] as CarBrand[],
        popularBrands: [] as CarBrand[]
      },
      slots: null as CalendarDay[] | null,
      mounted: false as boolean,
      initialSlotReserved: false,
      getSlotsInterval: null as any,
      showTextarea: false as boolean,
      repairDescription: '' as string
    };
  },
  computed: {
    brands(): SelectComponentItem[] {
      const popularBrands = this.carBrowser.popularBrands;

      const restBrands = this.carBrowser.brands.filter(brand => {
        return !!popularBrands.every(popularBrand => {
          return popularBrand.id !== brand.id;
        });
      });

      const brands = [...popularBrands, ...restBrands].map(brand => {
        return {
          key: brand.id,
          text: brand.name
        };
      });

      return brands;
    },
    models(): SelectComponentItem[] {
      return this.brand?.key
        ? this.carBrowser.brands.filter(brand => this.brand.key === brand.id)[0].models.map(brand => {
          return {
            key: brand.id,
            text: brand.name
          };
        })
        : [];
    },
    slotSelected(): string {
      return this.slotsSelected.length ? this.slotsSelected[0].fullDate : '';
    },
    calendarArray(): CalendarDay[] {
      return this.slots || [];
    },
    formInvalid(): boolean {
      return !this.model?.text || !this.brand?.text || (!this.service?.text && this.repairReasonId !== '2fb8fb93-ef7c-47a4-998d-2b4a8fa29f66') || !this.slotsSelected.length || (this.repairReasonId === '2fb8fb93-ef7c-47a4-998d-2b4a8fa29f66' && this.repairDescription.length < 10) || (this.repairReasonId !== '2fb8fb93-ef7c-47a4-998d-2b4a8fa29f66' && this.service?.key === 'disabled');
    },
    dateFrom(): string {
      return getDateFrom(0);
    },
    dateTo(): string {
      return getDateFrom(13);
    },
    source(): string {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('source') || 'sidebar';
    },
    showDeliveryDateInfo(): boolean {
      const slotSelected = this.slotsSelected[0];
      if (!slotSelected) return false;
      return !slotSelected.availableWorkspaces?.find((w: any) => w.type !== 'quantitative');
    },
    mechanicMeta(): IMechanicMeta {
      return JSON.parse(this.dataMechanicMeta);
    },
    redirect(): string {
      return bookowarkaURL + '/flow/' + (this.repairReasonId === 'dbb83bd4-e475-499a-9f9a-79a74654b7c5' ? '9f7b50c5-791c-4e64-a661-4ac789f1b1ac' : '473f9363-aeac-4f3f-8fed-e772574124bf');
    }
  },
  watch: {
    repairReasonId() {
      this.slots = null;
      this.GETslots();
      this.slotsSelected = [];
      this.service = fieldInit;
      if (this.initialSlotReserved) {
        this.initialSlotReserved = false;
      }
      if (this.$refs.calendar) {
        (this.$refs.calendar as any).resetControler();
      }
      if (this.repairReasonId === '2fb8fb93-ef7c-47a4-998d-2b4a8fa29f66') {
        this.service = (this.services[0] as SelectComponentItem);
      }
    },
    brand: {
      handler() {
        this.clearModel();
      },
      deep: true
    }
  },
  async mounted() {
    this.mounted = true;
    await this.GETslots();
    await this.GETcarBrowser();
    this.dataSlotsInterval ? this.GETslotsInterval() : this.GETslots();
    this.setInitialData();
  },
  beforeUnmount() {
    clearInterval(this.getSlotsInterval);
  },
  methods: {
    onUpdateSelect(service: any) {
      if (service.key === 'disabled') {
        this.service = fieldInit;
      }
    },
    async GETcarBrowser(): Promise<void> {
      const { data } = await HTTP('/api_autobooking/cars.json');
      this.carBrowser = data;
    },
    async GETslots(): Promise<void> {
      if (!this.repairReasonId) return;
      const { data } = await HTTP(
        `/api_autobooking/slots/serviceUuid:${this.repairReasonId}/mechanicId:${this.dataMechanicId}/from:${getDateFrom(0)}/to:${getDateFrom(13)}.json`
      );
      this.slots = mapCalendarSlots(data);
    },
    GETslotsInterval() {
      this.getSlotsInterval = setInterval(() => this.GETslots(), this.dataSlotsInterval);
    },
    clearModel(): void {
      if (this.model) {
        this.model.key = '';
        this.model.text = '';
      }
    },
    onSlotChange(ev: Slot): void {
      this.slotsSelected = [ev];

      if (this.initialSlotReserved) {
        this.initialSlotReserved = false;
      }
    },
    onBrandChosen(e: SelectComponentItem): void {
      this.brand = e;
      this.clearModel();
    },
    onModelChosen(e: SelectComponentItem): void {
      this.model = e;
    },
    onServiceChosen(e: SelectComponentItem): void {
      this.service = e;
    },
    setInitialData() {
      this.setInitialDate();
      this.setInitialService();
      this.setCarData();
    },
    setCarData() {
      this.brand = {
        key: this.dataBrandId || '',
        text: this.dataBrand || ''
      };
      setTimeout(() => {
        this.model = {
          text: this.dataModel || '',
          key: this.dataModelId || ''
        };
      }, 0);
    },
    setInitialDate() {
      if (!!this.dataInitialDate && this.initialCategoryId === this.repairReasonId && !!this.slots) {
        let slot: Slot | undefined;

        for (const d of this.slots) {
          for (const s of d.slots) {
            if (s.fullDate === this.dataInitialDate) {
              slot = s;
            }
          }
        }

        if (slot && slot.status === SlotStatus.AVAILABLE) {
          this.slotsSelected = [slot];
          (this.$refs.calendar as any).scrollToChosenSlot();
        } else {
          this.initialSlotReserved = true;
        }
      }
    },
    setInitialService() {
      if (this.dataInitialService) {
        const initialService = this.services.find((service: any) => service.key === this.dataInitialService);
        if (initialService) {
          this.service = (initialService as SelectComponentItem);
        }
      }

      if (this.repairReasonId === '2fb8fb93-ef7c-47a4-998d-2b4a8fa29f66') {
        this.service = (this.services[0] as SelectComponentItem);
      }
    }
  }
});
